import { useRef, useState, useContext } from "react";
import { IdleTimerProvider, workerTimers } from "react-idle-timer";
import { t } from 'i18next';
import {
  UserDetails,
  setLocalStorageBoolean,
  SignOutHandler,
  deleteEncryptedValue,
  NavigateConst,
  ANALYTICS_MESSAGES,
  useAnalytics,
} from 'common';
import {
  Button,
  ModalMultiUse,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { GlobalContext } from 'context';
import { useRefreshToken } from '@sentara-health/sentara-api-hooks-core';
import { stopOpenReplaySession } from 'common/openreplay/openreplay';


const IDLE_TRACKING_EVENTS = [
  'keydown',
  'wheel',
  'DOMMouseScroll',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'MSPointerDown',
  'MSPointerMove',
] as const;

const IdleTimeoutContainer = () => {
  const idleTimerRef = useRef(null);
  const [isIdle, setIsIdle] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const { getFeature } = useContext(GlobalContext);
  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const signOut = SignOutHandler();
  const { refreshToken } = useRefreshToken();

  // Initialize continue session handler function
  const handleContinueSession = async () => {
    setModalIsOpen(false);
    const ciamEnabled = getFeature?.isModernizedAPI;
    if (ciamEnabled) {
      try {
        const response = await refreshToken();
        if (
          response?.data?.latestAccessTime === null &&
          response?.data?.maxIdleExpirationTime === null &&
          response?.data?.maxSessionExpirationTime === null
        ) {
          // Handle other status codes or errors
          alert();
        }
      } catch (error) {
        console.error('Error while handleContinueSession:', error);
        alert();
      }
    }
    if (sessionTimeoutRef.current !== null) {
      clearTimeout(sessionTimeoutRef.current);
    }
  };

  const handleOnIdle = () => {
    setIsIdle(true);
    setModalIsOpen(false);
    sessionTimeoutRef.current = setTimeout(alert, 60000);
  };

  const alert = () => {
    const savedLanguage = localStorage.getItem('userLanguagePreference');
    localStorage.clear();
    if (savedLanguage) {
      localStorage.setItem('userLanguagePreference', savedLanguage);
    }
    deleteEncryptedValue();
    stopOpenReplaySession();
    window.location.href = NavigateConst.signIn;
    setModalIsOpen(false);
    if (isIdle && sessionTimeoutRef.current !== null) {
      clearTimeout(sessionTimeoutRef.current);
    }
    setLocalStorageBoolean(UserDetails?.inActiveSignOut, true);
  };

  const handleOnActive = () => {
    setIsIdle(false);
  };

  const { trackContentListing } = useAnalytics();
  const handleOnAction = (action: any) => {
    if (action?.continueSession) {
      trackContentListing(
        '405',
        ANALYTICS_MESSAGES.POP_USER_CONTINUE_SESSION,
        1
      );
      handleContinueSession();
    } else if (action?.signOut) {
      trackContentListing(
        '406',
        ANALYTICS_MESSAGES.POP_USER_END_SESSION,
        1
      );
      signOut();
    }
  };

  return (
    <IdleTimerProvider
      ref={idleTimerRef}
      timeout={1000 * 60 * 19}
      events={[...IDLE_TRACKING_EVENTS]}
      timers={workerTimers}
      onIdle={handleOnIdle}
      onActive={handleOnActive}
      onAction={handleOnAction}
    >
      <ModalMultiUse
        isOpen={modalIsOpen}
        header={t('dashboard.idleModalTitle')}
        body={
          <Paragraph
            text={t('dashboard.idleModalDescription')}
            variant="regular"
            data-testid="sessionModalDescription"
            className="mb-0"
          />
        }
        dataTestId={'sessionTimeoutModal'}
        variant="defaultNoPyBody"
        footer={
          <>
            <div className="col-md-6 col-12 mb-md-0 mb-3 ps-sm-0 pe-sm-2 px-0">
              <Button
                dataTestId={'continueSessionButton'}
                variant="primary_btn"
                className="btn-md-height"
                type="submit"
                onClick={() =>
                  handleOnAction({ continueSession: handleContinueSession })
                }
              >
                {t('common.continue')}
              </Button>
            </div>
            <div className="col-md-6 col-12 ps-sm-2 pe-sm-0 px-0">
              <Button
                dataTestId={'signOutButton'}
                variant="secondary_btn"
                className="btn-md-height"
                onClick={() => handleOnAction({ signOut: signOut })}
              >
                {t('dashboard.idleModalCancel')}
              </Button>
            </div>
          </>
        }
      />
    </IdleTimerProvider>
  );
};

export default IdleTimeoutContainer;